import { JstDate } from '../../core/time/business-time';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberService } from '../../core/services/member.service';
import { PagerComponent } from '../../shared/components/pager.component';
import Chart from 'chart.js';

@Component({
    selector: 'app-counsel-list',
    imports: [RouterLink, FormsModule, DatePipe, PagerComponent],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> カウンセリング一覧 <span>(全 {{ filteredItems.length }} 件)</span></span>
    </div>
    <div class="card-body">
      <div style="height: 1px"></div>
      <div class="row">
        <div class="col-md-3 text-md-right">会員氏名：</div>
        <div class="col-md-9 form-group">{{ member?.lastName }}&nbsp;{{ member?.firstName }}</div>
        <div class="col-md-3 text-md-right">区分コース：</div>
        <div class="col-md-9 form-group">{{ member?.studioPlan?.name }}</div>
        <div class="col-md-12 text-right">
          <a class="btn btn-primary text-white" [routerLink]="['/counsel', memberId, 'personal']" title="初回入力項目">
            <i class="fa s-icon-member fa-fw"></i><span>&nbsp;初回入力項目</span>
          </a>
        </div>
      </div>
      <hr />
    </div>
    <div class="card-body">
      <div class="form-group row">
        <div class="col-md-4">
          <label class="col-form-label" for="line_graph">グラフ表示</label>
          <select class="form-control" id="line_graph" name="line_graph" [(ngModel)]="selectedGraph" (ngModelChange)="updateGraph()">
            <option value="">-- 選択してください --</option>
            @for (opt of lineGraphOptions; track opt.id) {
              <option [value]="opt.id">{{ opt.name }}</option>
            }
          </select>
        </div>
      </div>
      @if (selectedGraph) {
        <div class="canvas-container" style="height: 300px;">
          <canvas id="myChart"></canvas>
        </div>
      }
    </div>
    <div class="card-body">
      <div class="form-group row">
        <div class="col-md-12">
          @if (personal?.id) {
            <a class="btn btn-primary text-white" [routerLink]="['/counsel', memberId, 'new']">
              <i class="fa s-icon-plus"></i>&nbsp;新規作成
            </a>
          }
        </div>
      </div>
      @if (searching) {
        <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
      }
      @if (!searching && filteredItems.length === 0) {
        <span>該当するデータはありません</span>
      }
      @if (filteredItems.length > 0) {
        <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
      } @else {
        <ul class="pagination pull-right"></ul>
      }
      <table class="table table-bordered table-sm table-hover table-ptb-0">
        @if (filteredItems.length > 0) {
          <thead>
            <tr>
              <th width="20%">実施日</th>
              <th width="30%">担当者</th>
              <th width="30%">実施メニュー</th>
              <th width="20%"></th>
            </tr>
          </thead>
        }
        <tbody>
          @for (item of pagedItems; track item.id) {
              <tr>
                <td>{{ item.counseledAt | date:'yyyy/MM/dd' }}</td>
                <td>{{ item.counselor }}</td>
                <td>{{ item.trainingProgram?.name }}</td>
                <td class="text-center">
                  <a class="mt-1 mb-1 btn btn-primary btn-sm text-white"
                    [routerLink]="['/counsel', item.id, 'edit']" title="編集">
                    <i class="fa s-icon-edit fa-fw"></i>
                  </a>
                </td>
              </tr>
          }
        </tbody>
      </table>
      @if (filteredItems.length > 0) {
        <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
      } @else {
        <ul class="pagination pull-right"></ul>
      }
    </div>
  </div>
</div>
  `
})
export class CounselListComponent implements OnInit, OnDestroy {
  private pageTitleService = inject(PageTitleService);
  private route = inject(ActivatedRoute);
  private memberService = inject(MemberService);

  memberId!: number;
  member: any = null;
  personal: any = null;
  items: any[] = [];
  searching = true;
  selectedGraph = '';
  private chart?: Chart;
  readonly pageLimit = 100;
  pageIndex = 0;

  lineGraphOptions = [
    { id: 1, name: '体重の推移', label: '体重（kg）', column: 'weight' },
    { id: 2, name: '体脂肪率の推移', label: '体脂肪率（％）', column: 'bodyFatPercentage' },
    { id: 3, name: '筋肉量の推移', label: '筋肉量（kg）', column: 'muscleMass' },
    { id: 4, name: '太もも周りの推移', label: '太もも周り（cm）', column: 'thighCircumference' },
    { id: 5, name: 'お尻周りの推移', label: 'お尻周り（cm）', column: 'hipCircumference' },
    { id: 6, name: 'お腹周りの推移', label: 'お腹周り（cm）', column: 'abdominalCircumference' },
    { id: 7, name: '二の腕周りの推移', label: '二の腕周り（cm）', column: 'armCircumference' },
  ];

  get filteredItems(): any[] { return this.items; }

  get pagedItems(): any[] {
    return this.filteredItems.slice(this.pageIndex * this.pageLimit, (this.pageIndex + 1) * this.pageLimit);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredItems.length / this.pageLimit);
  }

  goToPage(p: number): void {
    this.pageIndex = Math.max(0, Math.min(p, this.totalPages - 1));
  }

  updateGraph(): void {
    this.chart?.destroy();
    this.chart = undefined;
    if (!this.selectedGraph) return;

    window.requestAnimationFrame(() => {
      const option = this.lineGraphOptions.find(item => String(item.id) === String(this.selectedGraph));
      const canvas = document.getElementById('myChart') as HTMLCanvasElement | null;
      if (!option || !canvas) return;

      const values = this.items
        .map(item => Number.parseInt(String(item[option.column]), 10))
        .filter(value => Number.isFinite(value));
      const labels = this.items.map(item => this.formatDate(item.counseledAt)).reverse();
      while (labels.length < 14) labels.push(' ');
      const suggestedMin = values.length ? Math.min(...values) - 5 : 0;
      const suggestedMax = values.length ? Math.max(...values) + 5 : 0;

      this.chart = new Chart(canvas.getContext('2d')!, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: option.label,
            data: this.items.map(item => item[option.column]).reverse(),
            borderColor: 'rgba(230,26,123,1)',
            backgroundColor: 'rgba(230,26,123,1)',
            lineTension: 0,
            fill: false,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{
              type: 'linear',
              ticks: {
                suggestedMin,
                suggestedMax,
                stepSize: Math.max(1, Math.trunc((suggestedMax - suggestedMin) / 7)),
              },
            }],
          },
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private formatDate(value: string): string {
    if (!value) return '';
    const date = new JstDate(value);
    if (Number.isNaN(date.getTime())) return value.split('T')[0];
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('会員管理', 's-icon-members');
    this.memberId = +this.route.snapshot.params['memberId'];
    try {
      [this.member, this.items, this.personal] = await Promise.all([
        this.memberService.getOne(this.memberId),
        this.memberService.getCounsel(this.memberId),
        this.memberService.getPersonal(this.memberId),
      ]);
      this.items = this.items ?? [];
    } finally {
      this.searching = false;
    }
  }
}
